import { getDanhSachNhomKhachHang } from '@/apis/nhomKhachHangApi'
import type { INhomKhachHang } from '@/interfaces/nhomKhachHangInterfaces'
import type { NhomKhachHangUiTestItem } from '@/modules/ui-test/catalogs/nhomKhachHangUiTestCatalog'
import { buildMaSet, buildTenSet, normKey, stamp } from '@/modules/test-data/testDataSeedUtils'

export const UI_TEST_CREATE_COUNT = 2

export type NhomKhachHangUiTestPlan = {
  /** Nhóm sẽ thử tạo mỗi lần chạy — mã/tên unique theo DB hiện tại. */
  createItems: NhomKhachHangUiTestItem[]
  /** Mã dùng cho tìm kiếm / chọn dòng / sửa. */
  probeMa: string | null
  /** Id nhóm probe — liên kết nghiệp vụ theo id, mã có thể đổi khi sửa. */
  probeId: string | null
  /** Mã dùng cho xóa (ưu tiên nhóm vừa tạo, không trùng probe). */
  deleteMa: string | null
}

function newRunToken(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`
}

/** Sinh `count` nhóm mới, không trùng mã/tên đã có trên DB. */
export function generateUniqueCreateItems(
  existingMa: Set<string>,
  existingTen: Set<string>,
  count = UI_TEST_CREATE_COUNT,
): NhomKhachHangUiTestItem[] {
  const runId = newRunToken()
  const maSet = new Set(existingMa)
  const tenSet = new Set(existingTen)
  const items: NhomKhachHangUiTestItem[] = []
  let suffix = 1

  while (items.length < count && suffix < count + 100) {
    const ma = `UIT-NKH-${runId}-${suffix}`
    const tenNhom = `Test UI ${runId} — nhóm ${suffix}`
    const mk = normKey(ma)
    const tk = normKey(tenNhom)
    if (!maSet.has(mk) && !tenSet.has(tk)) {
      items.push({
        ma,
        tenNhom,
        ghiChu: `Sinh khi chạy test — ${new Date().toLocaleString('vi-VN')}`,
      })
      maSet.add(mk)
      tenSet.add(tk)
    }
    suffix += 1
  }

  return items
}

function pickRowMa(
  groups: INhomKhachHang[],
  options?: { preferUit?: boolean; excludeMa?: string | null },
): string | null {
  const exclude = normKey(options?.excludeMa ?? '')
  const withMa = groups.filter((g) => {
    const ma = g.ma?.trim()
    if (!ma) return false
    return normKey(ma) !== exclude
  })
  if (withMa.length === 0) return null

  if (options?.preferUit) {
    const uit = withMa.filter((g) => g.ma.startsWith('UIT-NKH-'))
    if (uit.length > 0) return uit[uit.length - 1].ma
  }

  return withMa[withMa.length - 1].ma
}

function firstExistingMa(groups: INhomKhachHang[], items: NhomKhachHangUiTestItem[]): string | null {
  for (const item of items) {
    if (groups.some((g) => normKey(g.ma) === normKey(item.ma))) return item.ma
  }
  return null
}

function resolveIdByMa(groups: INhomKhachHang[], ma: string | null): string | null {
  if (!ma) return null
  return groups.find((g) => normKey(g.ma) === normKey(ma))?.id ?? null
}

export function buildNhomKhachHangUiTestPlan(groups: INhomKhachHang[]): NhomKhachHangUiTestPlan {
  const existingMa = buildMaSet(groups.map((g) => ({ ma: g.ma })))
  const existingTen = buildTenSet(groups)
  const createItems = generateUniqueCreateItems(existingMa, existingTen, UI_TEST_CREATE_COUNT)

  const probeMa =
    firstExistingMa(groups, createItems.slice(0, 1)) ??
    pickRowMa(groups, { preferUit: true }) ??
    createItems[0]?.ma ??
    null

  const deleteMa =
    firstExistingMa(groups, createItems.slice(1, 2)) ??
    createItems[1]?.ma ??
    pickRowMa(groups, { preferUit: true, excludeMa: probeMa }) ??
    null

  return {
    createItems,
    probeMa,
    probeId: resolveIdByMa(groups, probeMa),
    deleteMa,
  }
}

/** Sau bước thêm / reload — chọn lại dòng thật trên DB cho các bước sau. */
export async function refreshNhomKhachHangUiTestPlan(plan: NhomKhachHangUiTestPlan): Promise<void> {
  const { data: groups } = await getDanhSachNhomKhachHang()
  const createdProbe = firstExistingMa(groups, plan.createItems.slice(0, 1))
  const createdDelete = firstExistingMa(groups, plan.createItems.slice(1, 2))

  plan.probeMa =
    createdProbe ?? pickRowMa(groups, { preferUit: true }) ?? plan.createItems[0]?.ma ?? plan.probeMa

  plan.probeId = resolveIdByMa(groups, plan.probeMa)

  plan.deleteMa =
    createdDelete ??
    (plan.createItems[1]?.ma && normKey(plan.createItems[1].ma) !== normKey(plan.probeMa ?? '')
      ? plan.createItems[1].ma
      : null) ??
    pickRowMa(groups, { preferUit: true, excludeMa: plan.probeMa }) ??
    plan.deleteMa
}

/** Mã mới khi test sửa — ngắn, unique trên DB (liên kết nghiệp vụ theo id). */
export function generateEditMa(_currentMa: string, existingMa: Set<string>): string {
  const token = stamp()
  let suffix = 0
  while (suffix < 50) {
    const ma = suffix === 0 ? `UIT-NKH-E${token}` : `UIT-NKH-E${token}${suffix}`
    if (!existingMa.has(normKey(ma))) return ma
    suffix += 1
  }
  return `UIT-NKH-E${token}${Date.now()}`
}

export function describeNhomKhachHangUiTestPlan(plan: NhomKhachHangUiTestPlan): string {
  const create = plan.createItems.map((i) => i.ma).join(', ') || '—'
  return `Thêm: [${create}] · Tìm/sửa: ${plan.probeMa ?? '—'} · Xóa: ${plan.deleteMa ?? '—'}`
}
